import classNames from 'classnames';
import React, { useCallback } from 'react';
import { useAppName } from '../../scripts/hooks/apps';
import { LinkFavicon } from '../controls/LinkFavicon/LinkFavicon';
import { UIIcon } from '../controls/ui/UIIcon/UIIcon';
import { ConnectedApps } from '../jit-qa/textInputBox/QATextInputView';

interface IUserAppsSelectProps {
  sortedInstantConnectedApps: ConnectedApps[];
  searchFilters: ConnectedApps[];
  setSearchFilters: React.Dispatch<React.SetStateAction<ConnectedApps[]>>;
}

export const UserAppsSelect: React.FC<IUserAppsSelectProps> = ({
  sortedInstantConnectedApps,
  searchFilters,
  setSearchFilters,
}) => {
  const { getAppName } = useAppName();

  const handleCheckChange = useCallback(
    (app: ConnectedApps) => {
      const checked = searchFilters.some((obj) =>
        obj.id ? obj.id === app.id : obj.appName === app.appName
      );

      if (checked) {
        setSearchFilters((prev) =>
          prev.filter((item) =>
            item.id ? item.id !== app.id : item.appName !== app.appName
          )
        );
      } else {
        setSearchFilters((prev) => [...prev, app]);
      }
    },

    [searchFilters, setSearchFilters]
  );

  const displayName = useCallback(
    (app: ConnectedApps) => {
      if (app.connectionName && app.connectionName !== '') {
        return app.connectionName;
      }

      const appName = getAppName(app.id);
      if (appName) {
        return appName;
      }

      return app.displayName.split(' ').slice(0, 2).join(' ');
    },
    [getAppName]
  );

  return (
    <>
      <div className="border-b max-h-[300px] overflow-y-auto">
        {sortedInstantConnectedApps.map((app) => {
          const selected = searchFilters.some((obj) =>
            obj.id ? obj.id === app.id : obj.appName === app.appName
          );

          return (
            <li
              className={classNames('gap-3', {
                appSelected: selected,
              })}
              key={app.id}
              onClick={() => {
                handleCheckChange(app);
              }}
            >
              <div className="iconName">
                {app.appName === 'website' ? (
                  <LinkFavicon
                    link={`http://${app.workspaceName!}`}
                    size={20}
                  />
                ) : (
                  <UIIcon name={app.appName} type="apps" />
                )}
                <span>{displayName(app)}</span>
              </div>
              <UIIcon
                className={`${selected ? '' : 'invisible'}`}
                name="check"
              />
            </li>
          );
        })}
      </div>
      <div className="controlButtons">
        <div
          className={classNames(
            {
              'text-cloud-20':
                searchFilters.length === sortedInstantConnectedApps.length,
            },
            'font-medium'
          )}
          onClick={() => {
            setSearchFilters(sortedInstantConnectedApps);
          }}
        >
          Select all
        </div>
        <div className="text-cloud-15">|</div>
        <div
          className={classNames(
            {
              'text-cloud-20': searchFilters.length === 0,
            },
            'font-medium'
          )}
          onClick={() => {
            setSearchFilters([]);
          }}
        >
          Clear all
        </div>
      </div>
    </>
  );
};
